// @ts-nocheck

const CTR_MM_V2_TRAP_OWNER = "ctr-mobile-menu-v2";
const FOCUS_TRAP_REGISTRY_KEY = "__exaltActiveFocusTrap";

/** @type {Record<string, (event: Event) => void>} */
const ctrMmV2FocusHandlers = {};

function ctrMmV2GetRegisteredTrap() {
  return document[FOCUS_TRAP_REGISTRY_KEY];
}

function ctrMmV2RegisterTrap(remove) {
  document[FOCUS_TRAP_REGISTRY_KEY] = { owner: CTR_MM_V2_TRAP_OWNER, remove };
}

function ctrMmV2UnregisterTrap() {
  const registered = ctrMmV2GetRegisteredTrap();
  if (registered?.owner === CTR_MM_V2_TRAP_OWNER) {
    delete document[FOCUS_TRAP_REGISTRY_KEY];
  }
}

function ctrMmV2RemoveRegisteredTrapIfNeeded() {
  const registered = ctrMmV2GetRegisteredTrap();
  if (
    registered &&
    registered.owner !== CTR_MM_V2_TRAP_OWNER &&
    typeof registered.remove === "function"
  ) {
    registered.remove();
  }
}

/**
 * @param {HTMLElement} container
 * @returns {HTMLElement[]}
 */
function ctrMmV2GetFocusable(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), input:not([type=hidden]):enabled, select:enabled, textarea:enabled"
    )
  ).filter(
    (el) =>
      el instanceof HTMLElement &&
      !el.hasAttribute("disabled") &&
      el.getAttribute("aria-disabled") !== "true"
  );
}

/**
 * @param {HTMLElement} container
 */
function ctrMmV2TrapFocus(container) {
  ctrMmV2RemoveRegisteredTrapIfNeeded();
  ctrMmV2RemoveTrapFocus();

  const focusable = ctrMmV2GetFocusable(container);
  if (!focusable.length) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  ctrMmV2FocusHandlers.keydown = (event) => {
    if (!(event instanceof KeyboardEvent) || event.key !== "Tab") return;

    const activeEl = document.activeElement;

    if (!event.shiftKey && activeEl === last) {
      event.preventDefault();
      first.focus();
    } else if (
      event.shiftKey &&
      (activeEl === first || activeEl === container)
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  ctrMmV2FocusHandlers.focusin = (event) => {
    if (
      event.target instanceof Node &&
      !container.contains(event.target)
    ) {
      if (document.activeElement === first) return;
      event.stopPropagation();
      first.focus();
    }
  };

  document.addEventListener("keydown", ctrMmV2FocusHandlers.keydown, true);
  document.addEventListener("focusin", ctrMmV2FocusHandlers.focusin, true);
  ctrMmV2RegisterTrap(ctrMmV2RemoveTrapFocus);
  first.focus();
}

function ctrMmV2RemoveTrapFocus() {
  if (ctrMmV2FocusHandlers.keydown) {
    document.removeEventListener(
      "keydown",
      ctrMmV2FocusHandlers.keydown,
      true
    );
  }
  if (ctrMmV2FocusHandlers.focusin) {
    document.removeEventListener(
      "focusin",
      ctrMmV2FocusHandlers.focusin,
      true
    );
  }
  ctrMmV2FocusHandlers.keydown = undefined;
  ctrMmV2FocusHandlers.focusin = undefined;
  ctrMmV2UnregisterTrap();
}

document.addEventListener("alpine:init", () => {
  Alpine.data("ctrMobileMenuV2", function () {
    return {
      isOpen: false,
      openParent: null,
      openSubmenu: null,
      _lastFocus: null,

      init() {
        /* The mobile-menu-v2 body class is emitted SERVER-SIDE by Liquid from
           settings.mobile_menu_v2 (layout/theme.liquid:928). Do not add it here.

           This used to also be added from a per-section `preview_enable` setting,
           which existed only to force the class on for QA while the Intelligems
           A/B ran. That test finished and v2 was rolled out (Charlie, 3 Sep 2026),
           and the hook was actively harmful: with it set, turning
           settings.mobile_menu_v2 OFF to fall back to the legacy drawer did not
           work. Liquid correctly stopped emitting the class and rendered the
           legacy markup, then this line put the class straight back and the CSS
           `display: none !important` hid the drawer Liquid had just restored -
           the exact opposite of what that setting's own help text promises. */

        this.$refs.drawer?.addEventListener("click", (event) => {
          const target = event.target;
          if (!(target instanceof Element)) return;
          const link = target.closest("a[href]");
          if (!link) return;
          const href = link.getAttribute("href");
          if (!href || href === "#" || href.startsWith("javascript:")) return;
          this.close();
        });

        this._bindLegacyTrigger();
        this._bindThemeEditor();
        this._warmThumbs();
        this._markThumbsLoaded();
      },

      /* R61 (8 Sep 2026): the green tile behind each parent thumbnail is applied only once the
         image has loaded (.ctr-mm-v2__parent-thumb.is-loaded, assets/ctr-mobile-menu-v2.css).
         Listeners are attached at init, before the drawer can open, so an image that lands
         while the drawer is closed is marked too. `complete && naturalWidth` covers an image
         already in cache; the load event covers the rest. Charlie, 8 Sep: "would rather just
         no green if loading". */
      _markThumbsLoaded() {
        const drawer = this.$refs.drawer;
        if (!(drawer instanceof HTMLElement)) return;
        drawer.querySelectorAll(".ctr-mm-v2__parent-thumb img").forEach((img) => {
          const tile = img.closest(".ctr-mm-v2__parent-thumb");
          if (!tile) return;
          const mark = () => tile.classList.add("is-loaded");
          if (img.complete && img.naturalWidth > 0) {
            mark();
          } else {
            img.addEventListener("load", mark, { once: true });
          }
        });
      },

      /* R59 (7 Sep 2026) + R59a (8 Sep 2026): fetch the five parent thumbnails BEFORE the drawer
         is opened, so they are already in cache when it is.

         Charlie, on his phone, 7 Sep: "green flash was mobile menu images loading". The tile
         behind each thumbnail is #00b67a (assets/ctr-mobile-menu-v2.css, .ctr-mm-v2__parent-thumb)
         and the drawer is `content-visibility: hidden` until it opens (ef84312, 25 Aug), so the
         images only start downloading on the tap. Measured on an emulated phone on Slow 4G:
         all five tiles solid green for ~1.1-1.7 s after the drawer became visible.

         R59 waited for the window `load` event. Charlie still saw green on 8 Sep, and the
         measurement explains it: on live, cold, Slow 4G / 4x CPU, DOMContentLoaded lands at
         7.7 s, the hero image is complete at 4.4 s, and `load` does not fire until 19.4 s. The
         drawer is tappable from DCL, so for the first ~12 s of a usable menu the warm-up had
         not run and a tap got 1.7 s of green. `load` is the wrong signal on a slow link.

         R59a runs from init() instead - Alpine initialises after DCL, which is the earliest
         moment the hamburger works at all - gated on every fetchpriority=high image on the page
         having finished (or a hard 2 s cap, whichever is first), so the ~43 KB never competes with the LCP
         image. On the homepage the hero is done ~3 s before DCL, so the gate is a no-op there;
         on a PDP the hero and DCL land close together and the gate holds the thumbnails back
         until the hero is in. Skipped when the browser asks for reduced data. The probe copies
         the real element's sizes and srcset so the browser resolves the SAME candidate URL it
         will pick when the drawer opens; a plain `src` warm-up would fetch a different width
         and warm nothing.

         The green itself must stay: two of the five thumbnails are transparent cut-outs
         (132x130 with alpha) drawn ON that green by design. */
      _warmThumbs() {
        const conn = navigator.connection;
        if (conn && conn.saveData) return;
        let done = false;
        const run = () => {
          if (done) return;
          done = true;
          const drawer = this.$refs.drawer;
          if (!(drawer instanceof HTMLElement)) return;
          drawer.querySelectorAll(".ctr-mm-v2__parent-thumb img").forEach((img) => {
            const probe = new Image();
            const sizes = img.getAttribute("sizes");
            const srcset = img.getAttribute("srcset");
            const src = img.getAttribute("src");
            if (sizes) probe.sizes = sizes;
            if (srcset) probe.srcset = srcset;
            if (src) probe.src = src;
          });
        };
        const idle = () => {
          if (typeof window.requestIdleCallback === "function") {
            window.requestIdleCallback(run, { timeout: 1000 });
          } else {
            setTimeout(run, 300);
          }
        };
        /* Wait for EVERY fetchpriority=high image, not the first: `sections/hero.liquid` emits a
           desktop and a mobile <img> both at high priority, and querySelector() would pick the
           hidden desktop one while the visible mobile LCP image is still in flight (Codex,
           round R59a, finding 2). The 2 s cap calls run() directly - not idle() - so it is a
           hard ceiling rather than 2 s plus an idle timeout (finding 1). */
        const heroes = [...document.querySelectorAll('img[fetchpriority="high"]')].filter(
          (img) => !img.complete
        );
        if (heroes.length === 0) {
          idle();
          return;
        }
        let pending = heroes.length;
        const settle = () => {
          pending -= 1;
          if (pending === 0) idle();
        };
        heroes.forEach((img) => {
          img.addEventListener("load", settle, { once: true });
          img.addEventListener("error", settle, { once: true });
        });
        setTimeout(run, 2000);
      },

      _bindThemeEditor() {
        if (typeof Shopify === "undefined" || !Shopify.designMode) return;

        const isOurs = (target) => {
          if (!(target instanceof Element)) return false;
          return (
            target === this.$el ||
            target.contains(this.$el) ||
            this.$el.contains(target)
          );
        };

        const expandFor = (target) => {
          if (!(target instanceof Element)) return;

          const parent = target.closest(".ctr-mm-v2__parent");
          if (parent && parent.dataset.parentId) {
            this.openParent = parent.dataset.parentId;
          }

          const childMenu = target.closest(".ctr-mm-v2__child-item--menu");
          if (childMenu) {
            const grandchildren = childMenu.querySelector(
              ".ctr-mm-v2__grandchildren"
            );
            if (grandchildren && grandchildren.id) {
              this.openSubmenu = grandchildren.id.replace(
                "ctr-mm-v2-submenu-",
                ""
              );
            }
          }
        };

        const onSelect = (event) => {
          if (!isOurs(event.target)) {
            if (this.isOpen) this.close();
            return;
          }
          if (!this.isOpen) this.open();
          expandFor(event.target);
        };

        const onDeselect = (event) => {
          if (!isOurs(event.target)) return;
          if (this.isOpen) this.close();
        };

        document.addEventListener("shopify:section:select", onSelect);
        document.addEventListener("shopify:section:load", onSelect);
        document.addEventListener("shopify:block:select", onSelect);
        document.addEventListener("shopify:section:deselect", onDeselect);
      },

      _bindLegacyTrigger() {
        const summaries = document.querySelectorAll(
          ".header__drawer--mobile .header__icon--menu.header__icon--summary"
        );

        summaries.forEach((summary) => {
          if (summary.dataset.ctrMmV2Bound === "true") return;
          summary.dataset.ctrMmV2Bound = "true";

          summary.addEventListener(
            "click",
            (event) => {
              if (!document.body.classList.contains("mobile-menu-v2")) return;
              event.preventDefault();
              event.stopImmediatePropagation();
              this.toggle();
            },
            true
          );
        });
      },

      toggle() {
        if (this.isOpen) {
          this.close();
        } else {
          this.open();
        }
      },

      open() {
        this._lastFocus = document.activeElement;
        this.isOpen = true;
        // R53 (6 Sep 2026): `scroll-lock` is set in $nextTick, NOT synchronously.
        // assets/base.css:75-80 makes html[scroll-lock] `position: fixed` under 750px,
        // and position:fixed on the ROOT element forces a whole-document relayout of
        // 428-819 KB of HTML. Setting it inside the click handler put that relayout on
        // the interaction's critical path: BigQuery RUM, deduped on wv_id, mobile
        // Chromium only, 30 Aug - 5 Sep 2026, has summary.header__icon--menu at
        // 208 ms p75 on 100 interactions - ~100 of the ~145 mobile Chromium
        // interactions above 200 ms are this one control. The 3 Sep deletion of the
        // 352-node legacy drawer moved it 192 -> 208 ms, i.e. nothing: the drawer's
        // own size was never the cost. INP measures to the next paint, so deferring
        // the attribute by one frame takes the relayout out of the measured window.
        // The `position: fixed` itself stays - it is what stops iOS scrolling the
        // background and what restores the scroll position on close. close() is
        // deliberately unchanged so the two arms can be told apart in the field.
        this.$nextTick(() => {
          document.documentElement.setAttribute("scroll-lock", "");
          if (this.$refs.drawer instanceof HTMLElement) {
            ctrMmV2TrapFocus(this.$refs.drawer);
          }
        });
      },

      close() {
        this.isOpen = false;
        this.openParent = null;
        this.openSubmenu = null;
        document.documentElement.removeAttribute("scroll-lock");
        ctrMmV2RemoveTrapFocus();
        if (this._lastFocus instanceof HTMLElement) {
          this._lastFocus.focus();
        }
      },

      /**
       * @param {string} parentId
       */
      toggleParent(parentId) {
        this.openParent = this.openParent === parentId ? null : parentId;
        this.openSubmenu = null;
      },

      /**
       * @param {string} submenuId
       */
      toggleSubmenu(submenuId) {
        this.openSubmenu = this.openSubmenu === submenuId ? null : submenuId;
      },
    };
  });
});
